'use client';

import { useEffect } from 'react';
import { unwrapResultSuccess } from '@headless-workspace/core-domain';
import { DataLayerKey, isVideo, useBasketProductsList, useDataLayer } from '@headless-workspace/domain/common/client';
import { CapacityFormat, ProductDetailValue, PromotionType } from '@headless-workspace/domain/common/server';
import { Component } from '@headless-workspace/typings';
import { ArrayHelpers, ValueHelpers } from '@headless-workspace/utils';

type ProductDetailTemplateDidMountProps = {
    product: ProductDetailValue;
};

const BREADCRUMB_ITEM_SEPARATOR = '/';

const getProductBreadcrumbLabels = (breadcrumb: ProductDetailValue['breadcrumb']) =>
    breadcrumb.map((breadcrumb) => breadcrumb.tcDisplayName).filter((tcDisplayName) => tcDisplayName !== '');

export const ProductDetailTemplateDidMount: Component<ProductDetailTemplateDidMountProps> = ({ product }) => {
    const { addVariable, addVariables } = useDataLayer();

    const { result: basketResult } = useBasketProductsList();

    const basketData = unwrapResultSuccess(basketResult);
    const customized = ArrayHelpers.matches(basketData?.products, (basketProduct) => {
        return (
            basketProduct?.productId === product.id &&
            Array.isArray(basketProduct.engraves) &&
            basketProduct.engraves.length > 0
        );
    });

    const brandName = product.brand.name;
    const productBreadcrumbId = product.breadcrumb.filter((_, index) => index !== 0).map((item) => item.id);
    const hasDiscount = ValueHelpers.mapYesNo(!!product.priceBeforeDiscount);
    const isCustomizable = ValueHelpers.mapYesNo(product.isEngravable);
    const isCustomized = customized ? 1 : 0;
    const inStock = ValueHelpers.mapYesNo(!!product.stockLevel);
    const hasTravelSize = ValueHelpers.mapYesNo(product.formats.includes(CapacityFormat.Mini));
    const hasVideo = ValueHelpers.mapYesNo(product.medias.some((media) => isVideo(media)));
    const hasDefaultSku = ValueHelpers.mapYesNo(!!product.masterProductId);
    const productPromotion = product.promotion === PromotionType.OTHER ? 'without promotion' : 'crossed out price';
    const productBreadCrumb = getProductBreadcrumbLabels(product.breadcrumb);
    const productBreadcrumbLabel = productBreadCrumb.join(BREADCRUMB_ITEM_SEPARATOR);
    const [topCat, cat, subCat] = productBreadCrumb;
    const productSkuName = `${product.id} - ${product.name?.full}`;

    const calculateDiscountAti = (originalPrice?: number, priorPrice?: number) => {
        return priorPrice && originalPrice ? originalPrice - priorPrice : 0;
    };

    const getProductNumRating = (reviewCount: number) => {
        if (reviewCount === 0) return '0';
        if (reviewCount <= 10) return '0-10';
        if (reviewCount <= 25) return '11-25';
        if (reviewCount <= 50) return '26-50';
        if (reviewCount <= 75) return '51-75';

        return '76+';
    };

    useEffect(() => {
        if (typeof document !== 'undefined') {
            addVariables({
                [DataLayerKey.PageName]: `${product.name?.full}|${product.id}`,
                [DataLayerKey.PageTopCat]: topCat ?? '',
                [DataLayerKey.PageCat]: cat ?? '',
                [DataLayerKey.PageSubCat]: subCat ?? '',
                [DataLayerKey.ProductBreadcrumbLabel]: productBreadcrumbLabel,
                [DataLayerKey.PageTrademark]: brandName,
                [DataLayerKey.ProductAxis]: product.axis,
                [DataLayerKey.ProductBrand]: brandName,
                [DataLayerKey.ProductBreadcrumbId]: productBreadcrumbId.toString(),
                [DataLayerKey.ProductCop]: hasDiscount,
                [DataLayerKey.ProductCurrency]: product.currency.toLowerCase(),
                [DataLayerKey.ProductCustomizable]: isCustomizable,
                [DataLayerKey.ProductCustomized]: isCustomized.toString(),
                [DataLayerKey.ProductDefaultSku]: hasDefaultSku,
                [DataLayerKey.ProductDiscountAti]: calculateDiscountAti(
                    product.originalPrice,
                    product.priorPrice,
                ).toString(),
                [DataLayerKey.ProductFidelityPoints]: product.loyalty?.pointsToEarn?.toString(),
                [DataLayerKey.ProductInstock]: inStock,
                [DataLayerKey.ProductNature]: product.nature,
                [DataLayerKey.ProductNumRating]: getProductNumRating(product.reviewCount),
                [DataLayerKey.ProductOldPriceAti]: product.priorPrice?.toString(),
                [DataLayerKey.ProductOldPriceTf]: product.vat?.toString(),
                [DataLayerKey.ProductPid]: product.masterProductId,
                [DataLayerKey.ProductPidName]: product.name?.full,
                [DataLayerKey.ProductPriceAti]: product.priorPrice?.toString(),
                [DataLayerKey.ProductPriceTf]: product.vat?.toString(),
                [DataLayerKey.ProductPromotion]: productPromotion,
                [DataLayerKey.ProductRange]: product.range,
                [DataLayerKey.ProductRating]: product.rating.toString(),
                [DataLayerKey.ProductSku]: product.sku,
                [DataLayerKey.ProductSkuName]: productSkuName,
                [DataLayerKey.ProductTrademark]: brandName,
                [DataLayerKey.ProductTravelSizeAvailability]: hasTravelSize,
                [DataLayerKey.ProductVideo]: hasVideo,
            });
        }
    }, [addVariable, DataLayerKey, product, customized]);

    return null;
};
